import { getAccessToken, shouldRefreshToken } from './tokenService';
import { refreshSession, signOut } from './sessionService';

export interface AuthApiOptions extends RequestInit {
  skipAuthCheck?: boolean;
  autoRedirect?: boolean;
}

export interface AuthApiResponse<T = unknown> {
  data: T;
  status: number;
  ok: boolean;
}

function handleAuthFailure(): void {
  if (typeof window !== 'undefined') {
    console.warn('Authentication required or token expired. Redirecting to login.');
    signOut();
    window.location.href = '/sign-in?status=session-expired';
  }
}

async function getValidToken(): Promise<string | null> {
  let token = getAccessToken();
  if (!token) return null;

  if (shouldRefreshToken(60)) {
    const result = await refreshSession();
    if (result.success && result.accessToken) {
      token = result.accessToken;
    }
  }

  return token;
}

export async function authFetch<T = unknown>(
  url: string,
  options: AuthApiOptions = {}
): Promise<T> {
  const { skipAuthCheck = false, autoRedirect = true, ...fetchOptions } = options;

  let token = await getValidToken();

  if (!token && !skipAuthCheck) {
    if (autoRedirect) handleAuthFailure();
    throw new Error('Access token is missing');
  }

  const requestOptions: RequestInit = {
    ...fetchOptions,
    headers: {
      ...fetchOptions.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  try {
    let response = await fetch(url, requestOptions);

    if (response.status === 401) {
      const refreshResult = await refreshSession();
      if (refreshResult.success && refreshResult.accessToken) {
        const retryOptions: RequestInit = {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            Authorization: `Bearer ${refreshResult.accessToken}`,
          },
        };

        response = await fetch(url, retryOptions);

        if (response.ok || response.status === 204) {
          if (response.status === 204) return null as T;
          return (await response.json()) as T;
        }
      }

      if (autoRedirect) handleAuthFailure();
      throw new Error('Unauthorized - please log in again');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `Request failed with status ${response.status}`,
      }));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }

    if (response.status === 204) return null as T;
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof Error && (error.message.includes('Unauthorized') || error.message.includes('expired'))) {
      throw error;
    }
    console.error('Auth fetch error:', error);
    throw error;
  }
}

export async function get<T = unknown>(url: string, options?: AuthApiOptions): Promise<T> {
  return authFetch<T>(url, { ...options, method: 'GET' });
}

export async function post<T = unknown>(url: string, body?: unknown, options?: AuthApiOptions): Promise<T> {
  return authFetch<T>(url, {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function put<T = unknown>(url: string, body?: unknown, options?: AuthApiOptions): Promise<T> {
  return authFetch<T>(url, {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function del<T = unknown>(url: string, options?: AuthApiOptions): Promise<T> {
  return authFetch<T>(url, { ...options, method: 'DELETE' });
}

export const authApi = { fetch: authFetch, get, post, put, delete: del };

export default authFetch;
